import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal, NativeSelect, Select } from '@material-ui/core';

import { packagingApi as api } from 'api/packaging';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

import { AddPackagingItems } from './addPackagingItems';
import { CreatePackagingPO } from './createPurchaseOrder';

function ViewItemsTable({ parentRow, setExpandedRow }) {
    const [poID, setPOID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [packagingOptions, setPackagingOptions] = useState([]);

    const getPackagingOptions = async () => {
        const itemMasterRes = await api.getPackaging();
        if (itemMasterRes.status === false) {
        } else {
            const items = itemMasterRes.data.rows.map((item) => ({
                text: `SKU ${item.model.masterSKU}: ${item.model.description}`,
                value: item.model.id,
            }));
            setPackagingOptions(items);
        }
    };

    const columns = [
        { accessor: 'masterSku', Header: 'Item SKU', editable: true },
        { accessor: 'description', Header: 'Description' },
        {
            accessor: 'unitsPerCase',
            Header: 'Units Per Case',
            editable: true,
            editProps: { type: 'input', inputType: 'number' },
        },
        { accessor: 'unitsPerCaseUOM', Header: 'Unit of Measure' },
        {
            accessor: 'numPallets',
            Header: 'Pallets per SKU',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'qty',
            Header: 'Quantity',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'vendorSku',
            Header: 'Vendor SKU',
            editable: true,
            editProps: { type: 'input', inputType: 'text' },
        },
        {
            accessor: 'unitPrice',
            Header: 'Unit Price',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        },
        {
            accessor: 'estimatedFreightCost',
            Header: 'Estimated Freight Cost',
            editable: true,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    onClick={async () => {
                        const response = await api.removeItemFromPO(poID, row.original.id);

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }

                        getItemsInTable();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    let getItemsInTable = async () => {
        const response = await api.getPurchaseOrderDetails(poID);

        if (response.status === false) {
            setNotification({ text: `No items found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        const poDetails = response.data.output;

        // Need to add id which will populate in saveEdit
        setRows(
            poDetails.items.map((row) => {
                row.id = row.poMapID;
                row.unitPrice = parseFloat(row.unitPrice).toFixed(2);
                return row;
            }),
        );
    };

    useEffect(() => {
        getItemsInTable();
        getPackagingOptions();
    }, []);

    return (
        <div className="batch-table">
            <Notifications options={notification} />
            {addItemModalOpen && (
                <AddItemModal
                    poID={poID}
                    packagingOptions={packagingOptions}
                    closeModal={() => {
                        setAddItemModalOpen(false);
                        getItemsInTable();
                    }}
                />
            )}
            <DataTable
                columns={columns}
                data={rows}
                editable
                saveEdit={async ({ field, id, value }) => {
                    const updates = { [field]: value };
                    const response = await api.updatePurchaseOrderMap(poID, id, updates);
                    if (response.status === false) {
                        let message = 'Error modifying packaging purchase order';
                        if (response) {
                            message += `: ${response.msg}`;
                        }
                        setNotification({ text: message, level: 'error' });
                    } else {
                        setNotification({ text: 'Packaging Purchase Order modified!', level: 'success' });
                    }
                    if (response.status === true && field === 'masterSku') {
                        getItemsInTable();
                    } else {
                        return response.status;
                    }
                }}
            />
            <Button
                onClick={() => {
                    setAddItemModalOpen(true);
                }}
            >
                Add Item
            </Button>
        </div>
    );
}

function AddItemModal({ closeModal, packagingOptions, poID }) {
    const [error, setError] = useState(null);

    const packagingFormFields = [
        {
            name: 'id',
            inputElement: 'autocomplete',
            label: 'Packaging',
            gridValue: 8,
            inputProps: { required: true, opts: packagingOptions },
        },
        {
            name: 'qty',
            inputElement: 'textField',
            label: 'Quantity (cases)',
            gridValue: 2,
            inputProps: { type: 'number', required: true },
        },
        {
            name: 'unitPrice',
            inputElement: 'textField',
            label: 'Unit Price',
            gridValue: 2,
            inputProps: { type: 'number', required: true },
        },
        {
            name: 'unitsPerCase',
            inputElement: 'textField',
            label: 'Units Per Case',
            gridValue: 3,
            inputProps: { type: 'number', required: true },
        },
        {
            name: 'unitsPerCaseUOM',
            inputElement: 'select',
            label: 'Unit Of Measure',
            gridValue: 3,
            inputProps: { opts: ['ct'], required: true },
        },
        {
            name: 'vendorSku',
            inputElement: 'textField',
            label: 'Vendor SKU',
            gridValue: 4,
            inputProps: { required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={packagingFormFields}
                    onSubmit={async (formData) => {
                        const items = {};
                        items.items = [formData];

                        const response = await api.addItemToPurchaseOrder(poID, items);
                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        closeModal();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditDeliveryModal({ closeModal, rowData }) {
    const [error, setError] = useState(null);

    const date = dayjs(rowData.scheduledDeliveryDate).format('YYYY-MM-DD');
    const time = dayjs(rowData.scheduledDeliveryDate).format('HH:mm');

    const formFields = [
        {
            name: 'poID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Purchase Order ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'scheduledDeliveryDate',
            initialValue: date,
            inputElement: 'date',
            disableTextInput: true,
            label: 'Scheduled Delivery Date',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'scheduledTime',
            initialValue: time,
            inputElement: 'time',
            label: 'Delivery time',
            gridValue: 4,
            inputProps: { required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.scheduledDeliveryDate = formData.scheduledDeliveryDate;
                        item.deliverTime = formData.scheduledTime;

                        const response = await api.updatePurchaseOrder(formData.poID, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                        } else {
                            closeModal();
                        }
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function ViewPackagingPO() {
    const [rows, setRows] = useState([]);
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [selectedRow, setSelectedRow] = useState({});
    const user = useContext(UserContext);

    // Load
    const getRows = async () => {
        const response = await api.getPurchaseOrders('', user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No POs found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(response.data.rows);
    };

    useEffect(() => {
        getRows();
    }, []);

    const closeDeliveryEditModal = () => {
        setEditDeliveryModalOpen(false);
    };

    const showDeliveryEditModal = (row, closeDeliveryEditModal) => {
        setSelectedRow(row);
        setEditDeliveryModalOpen(true);
    };

    const columns = [
        { accessor: 'id', Header: 'ID' },
        {
            accessor: 'scheduledDeliveryDate.date',
            Header: 'Scheduled Delivery',
            editable: true,
            customEdit: showDeliveryEditModal,
        },
        { accessor: 'status', Header: 'Status' },
        { accessor: 'vendorName', Header: 'Vendor' },
        { accessor: 'facilityName', Header: 'Facility' },
        { accessor: 'referenceNumber', Header: 'Reference', editable: true },
        {
            accessor: 'download',
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const [downloadOpt, setDownloadOpt] = useState('pdf');
                const downloadOptions = ['pdf', 'ship-list'];
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <NativeSelect
                            id={`Download_Select_${row.id}`}
                            className="input-element-select"
                            value={downloadOpt}
                            label="Download"
                            onChange={(event) => {
                                setDownloadOpt(event.target.value);
                            }}
                        >
                            {downloadOptions.map((value, i) => (
                                <option key={i} value={value}>
                                    {value}
                                </option>
                            ))}
                            ;
                        </NativeSelect>
                        <Button
                            id={`Download_${row.id}`}
                            onClick={() => {
                                if (downloadOpt === 'pdf') {
                                    api.downloadPackagingPOPDF(row.original.id, row.original.vendorName);
                                } else if (downloadOpt === 'ship-list') {
                                    api.downloadPackagingPOShipList(row.original.id);
                                } else {
                                    console.log(`Unrecognized download option ${downloadOpt}`);
                                }
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
        {
            accessor: 'send',
            Header: 'Send To Vendor',
            Cell: ({ row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Send_${row.id}`}
                        onClick={async () => {
                            const response = await api.sendPurchaseOrderToVendor(row.original.id);
                            if (response.status === false) {
                                setNotification({ text: response.msg, level: 'error' });
                                return;
                            }
                            setNotification({ text: 'Sent Packaging PO to Vendor', level: 'success' });
                        }}
                    >
                        SEND
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'cancel',
            Header: 'Cancel',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Cancel_${row.id}`}
                        onClick={async () => {
                            const response = await api.cancelPurchaseOrder(row.original.id);
                            if (response.status === false) {
                                setNotification({ text: `Failed to cancel PO ${response.msg}`, level: 'error' });
                                return;
                            }
                            setNotification({ text: 'Purchase Order cancelled', level: 'success' });
                            await getRows();
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <h1>Packaging Purchase Orders</h1>
            <Notifications options={notification} />
            {}
            <div>
                {editDeliveryModalOpen && (
                    <EditDeliveryModal
                        rowData={selectedRow}
                        closeModal={() => {
                            setEditDeliveryModalOpen(false);
                            getRows();
                        }}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={rows}
                    expandable
                    ExpansionComponent={<ViewItemsTable />}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        const item = {};
                        item[field] = value;
                        if (field == 'vendorName') {
                            item.vendorID = value;
                        }
                        const response = await api.updatePurchaseOrder(id, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Purchase Order modified!', level: 'success' });
                            await getRows();
                        }
                        return response.status;
                    }}
                    duplicateRowSettings={{
                        enable: true,
                        CustomFormComponent: <CreatePackagingPO />,
                        fetchAdditionalData: async (row) => {
                            const response = await api.getPurchaseOrderDetails(row.id);

                            if (!response.status) {
                                setNotification({ text: 'Failed to get items in purchase order', level: 'error' });
                                return [];
                            }

                            const poDetails = response.data.output;

                            const details = poDetails.items.map((row) => {
                                row.unitPrice = parseFloat(row.unitPrice).toFixed(2);
                                return row;
                            });
                            return details;
                        },
                        onSuccess: () => {
                            getRows();
                            setNotification({ text: 'Packaging Purchase Order Created', level: 'success' });
                        },
                    }}
                />
            </div>
        </div>
    );
}

export { ViewPackagingPO };
